import { useEffect } from 'react';
import useLatestRef from './useLatestRef';
export default function useDialog(options) {
    const latest = useLatestRef(options);
    useEffect(() => {
        const { dialog, container, initialFocus, bodyClassName, restoreFocusTo } = latest.current;
        const hadBodyClass = bodyClassName ? document.body.classList.contains(bodyClassName) : false;
        const scrollPosition = bodyClassName && !hadBodyClass
            ? { x: window.scrollX, y: window.scrollY, top: document.body.style.top, left: document.body.style.left }
            : null;
        if (scrollPosition) {
            // Keep the page stationary when iOS pans the viewport for the keyboard.
            document.body.style.top = `${-scrollPosition.y}px`;
            document.body.style.left = `${-scrollPosition.x}px`;
        }
        if (bodyClassName)
            document.body.classList.add(bodyClassName);
        initialFocus?.current?.focus({ preventScroll: true });
        const hiddenBackground = [];
        let ancestor = container?.current ?? dialog.current;
        while (ancestor?.parentElement) {
            const currentAncestor = ancestor;
            Array.from(ancestor.parentElement.children).forEach(element => {
                if (element !== currentAncestor && element instanceof HTMLElement) {
                    hiddenBackground.push({
                        element,
                        inert: element.hasAttribute('inert'),
                        ariaHidden: element.getAttribute('aria-hidden'),
                    });
                    element.setAttribute('inert', '');
                    element.setAttribute('aria-hidden', 'true');
                }
            });
            ancestor = ancestor.parentElement === document.body ? null : ancestor.parentElement;
        }
        const onKeyDown = (event) => {
            const scope = dialog.current;
            // A nested dialog owns keyboard interaction until it closes.
            if (!scope || event.defaultPrevented || scope.querySelector('[role="alertdialog"], [role="dialog"]'))
                return;
            if (event.key === 'Escape') {
                event.preventDefault();
                latest.current.onClose();
                return;
            }
            if (event.key !== 'Tab')
                return;
            const focusable = Array.from(scope.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(element => element.tabIndex >= 0 &&
                !element.matches(':disabled') &&
                !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
                element.getClientRects().length > 0);
            if (!focusable.length) {
                event.preventDefault();
                scope.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!scope.contains(document.activeElement)) {
                event.preventDefault();
                first?.focus();
            }
            else if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last?.focus();
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first?.focus();
            }
        };
        const touchRoot = container?.current ?? dialog.current;
        let touchPosition = null;
        const onTouchStart = (event) => {
            const touch = event.touches.length === 1 ? event.touches[0] : null;
            touchPosition = touch ? { x: touch.clientX, y: touch.clientY } : null;
        };
        const onTouchMove = (event) => {
            const previous = touchPosition;
            onTouchStart(event);
            if (!previous || !touchPosition || event.defaultPrevented)
                return;
            const deltaY = touchPosition.y - previous.y;
            if (!deltaY || Math.abs(touchPosition.x - previous.x) > Math.abs(deltaY))
                return;
            const target = event.target instanceof Element ? event.target : null;
            if (target && touchRoot?.contains(target)) {
                const scope = target.closest('[role="alertdialog"], [role="dialog"]') ?? touchRoot;
                for (let element = target; element; element = element.parentElement) {
                    const scrollable = /^(auto|scroll)$/.test(getComputedStyle(element).overflowY);
                    const remaining = element.scrollHeight - element.clientHeight;
                    if (scrollable && remaining > 0) {
                        if (deltaY > 0 && element.scrollTop > 0)
                            return;
                        if (deltaY < 0 && element.scrollTop < remaining - 1)
                            return;
                    }
                    if (element === scope)
                        break;
                }
            }
            // iOS can pan the keyboard viewport when a gesture escapes a fixed body's scroll areas.
            if (event.cancelable)
                event.preventDefault();
        };
        document.addEventListener('keydown', onKeyDown);
        if (bodyClassName) {
            document.addEventListener('touchstart', onTouchStart, { passive: true });
            document.addEventListener('touchmove', onTouchMove, { passive: false });
        }
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('touchstart', onTouchStart);
            document.removeEventListener('touchmove', onTouchMove);
            if (bodyClassName && !hadBodyClass)
                document.body.classList.remove(bodyClassName);
            if (scrollPosition) {
                document.body.style.top = scrollPosition.top;
                document.body.style.left = scrollPosition.left;
                if (window.scrollX !== scrollPosition.x || window.scrollY !== scrollPosition.y) {
                    window.scrollTo(scrollPosition.x, scrollPosition.y);
                }
            }
            hiddenBackground.forEach(({ element, inert, ariaHidden }) => {
                if (!inert)
                    element.removeAttribute('inert');
                if (ariaHidden === null)
                    element.removeAttribute('aria-hidden');
                else
                    element.setAttribute('aria-hidden', ariaHidden);
            });
            restoreFocusTo?.focus({ preventScroll: true });
        };
    }, [latest]);
}
